import { useEffect } from 'react';

/**
 * Потолок пути в долях окна. 0.6 — блок раскрыт, когда его верх поднялся до
 * 40% высоты экрана: к этому моменту его уже видно и ещё не пролистали.
 */
const VIEWPORT_CAP = 0.6;

/**
 * Доля пути, пройденная блоком по экрану, — в CSS-переменную (#1330).
 *
 * Блок «раскрывается» ровно настолько, насколько его подтянули прокруткой:
 * хук пишет в `--<name>-dock` число 0…1, а вся геометрия считается из него в
 * CSS. Это НЕ переход по порогу: порог щёлкает в одной точке, и дальше
 * анимация доигрывает свои миллисекунды сама по себе — независимо от того,
 * продолжают тянуть или уже остановились. Привязанная к прокрутке геометрия
 * замирает вместе с пальцем и не нуждается в сглаживании.
 *
 * Замер — в `requestAnimationFrame`: события `scroll` приходят чаще кадра, и
 * считать на каждом значит делать работу, которую никто не увидит.
 *
 * Дополнительно хук меряет ПОЛЯ до кромок окна (`--<name>-start` /
 * `--<name>-end`) — расстояние от собственного края блока до края
 * страницы. Из них блок умеет разъезжаться на всю ширину, не зная ничего про
 * контейнер, в котором лежит. Поля берутся у РОДИТЕЛЯ, а не у самого блока:
 * блок к этому моменту уже разъехался своими отрицательными полями, и мерить
 * по нему значило бы гоняться за собственным хвостом.
 *
 * Ширина страницы — `documentElement.clientWidth`, а не `innerWidth`: второй
 * считает вместе с полосой прокрутки, и блок вылезал бы за неё ровно на её
 * толщину, заводя горизонтальную прокрутку.
 *
 * Живёт в `pages/shared/` и в бандл дилера не едет. У подвала
 * (`components/Footer.jsx`) своя копия замера: он-то как раз едет, и тянуть
 * ради него витринный модуль в Shadow DOM нельзя.
 *
 * @param {import('react').RefObject<HTMLElement>} ref — за чем следим.
 * @param {object} [options]
 * @param {string} [options.name] — префикс переменных.
 * @param {number} [options.travel] — какую долю своей высоты блок проходит до
 *   полного раскрытия. 1 — раскрыт, когда виден целиком.
 * @param {boolean} [options.bleed] — мерить ли поля до кромок окна.
 */
export const useScrollDock = (
  ref,
  { name = 'dock', travel = 1, bleed = false } = {}
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const set = (suffix, value) =>
      el.style.setProperty(`--${name}${suffix}`, value);

    /**
     * Спокойное движение: блок сразу раскрыт.
     *
     * Проверка ЗДЕСЬ, а не только в CSS: `setProperty` пишет инлайн, а инлайн
     * побеждает любое правило таблицы стилей — ветка `prefers-reduced-motion`
     * с тем же именем переменной молча проиграла бы, и блок продолжил бы
     * ездить за прокруткой.
     */
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

    let ticking = false;
    const measure = () => {
      ticking = false;

      if (bleed) {
        const parent = el.parentElement;
        if (parent) {
          const page = document.documentElement.clientWidth;
          const box = parent.getBoundingClientRect();
          const pad = getComputedStyle(parent);
          set(
            '-start',
            `${Math.max(0, box.left + parseFloat(pad.paddingLeft))}px`
          );
          set(
            '-end',
            `${Math.max(
              0,
              page - (box.right - parseFloat(pad.paddingRight))
            )}px`
          );
        }
      }

      if (calm.matches) {
        set('-dock', '1');
        return;
      }

      const viewport = window.innerHeight;
      const { top, height } = el.getBoundingClientRect();
      /**
       * Путь — своя высота, но НЕ БОЛЬШЕ доли окна.
       *
       * Своя высота задаёт правильный смысл: блок раскрыт, когда виден
       * целиком, а не пока он ещё за нижней кромкой. Но у блока ВЫШЕ экрана
       * этой высоты просто нет в запасе: на 390px карточка звонка
       * вытягивается в колонку, стоит последней на странице, и прокрутка
       * упирается раньше, чем путь пройден (замер на самом низу: 0.74 — то
       * есть подложка так и не доезжала). Потолок в долю окна делает путь
       * достижимым на любом экране.
       */
      const distance = Math.min(height, viewport * VIEWPORT_CAP) * travel;
      const progress = distance > 0 ? (viewport - top) / distance : 1;
      set('-dock', String(Math.min(1, Math.max(0, progress))));
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    calm.addEventListener('change', measure);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      calm.removeEventListener('change', measure);
    };
  }, [ref, name, travel, bleed]);
};

export default useScrollDock;
